import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { WebhookDeliveryGridMode } from 'skladon-types';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import Table from 'src/components/Table';
import {
    CustomRenderType,
    OnChangeOptionsType,
    RowActionPropsType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType,
} from 'src/types/table';
import { useDebounce } from 'src/utils/debounce';
import { getWebhookDeliveriesGridFetch, retryWebhookDeliveryFetch } from 'src/resources/webhookDeliveriesGrid/reducer';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import TruncatedCopyCell from 'src/components/TruncatedCopyCell';
import { TableRouterLink } from 'src/components/TableLink';
import { ReloadIcon, WebhookEditIcon } from 'src/components/SkladonIcon/Icons';
import { attemptsColumns, groupedColumns, REQUIRED_WEBHOOK_DELIVERIES_GRID_COLUMNS } from './constants';
import { canRetryDelivery, getStatusColor, isAutoRetryInProgress, stringifyPayload } from './functions';
import { WebhookDeliveryRowType } from 'src/types/webhookDeliveriesGrid';
import { useCheckPermission } from 'src/utils/checkPermission';
import { getWebhookMethodSelectOptions, getWebhookTypeSelectOptions } from 'src/utils/webhookSelectOptions';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment';

export const SESSION_NAME = 'webhookRecordsTable';
export const MODE_SESSION_NAME = 'webhookRecordsTableMode';

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'SENDING_TIME', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: [],
};

const WebhookRecordsListContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');
    const sessionMode = (sessionStorage.getItem(MODE_SESSION_NAME) || 'grouped') as WebhookDeliveryGridMode;

    const [mode, setMode] = useState<WebhookDeliveryGridMode>(sessionMode);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);
    const [expandedEvents, setExpandedEvents] = useState<Record<string, boolean>>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const loading = useSelector((state: RootStoreType) => state.webhookDeliveriesGrid.loading);
    const deliveriesData = useSelector((state: RootStoreType) => state.webhookDeliveriesGrid.data);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);

    const permissionUpdate = useCheckPermission('WEBHOOK.UPDATE');
    const permissionDetail = useCheckPermission('WEBHOOK.DETAIL');

    const webhookTypeCodes = useMemo(() => getWebhookTypeSelectOptions(), []);
    const webhookMethodCodes = useMemo(() => getWebhookMethodSelectOptions(), []);

    const tableColumns: TableColumnType[] = useMemo(
        () => (mode === 'grouped' ? groupedColumns : attemptsColumns),
        [mode]
    );

    const displayRows = useMemo(() => {
        const sourceRows = deliveriesData?.data || [];
        if (mode !== 'grouped') {
            return sourceRows;
        }

        const rows: TableRowType[] = [];
        sourceRows.forEach((row) => {
            rows.push(row);
            if (expandedEvents[String(row.EVENT_ID)]) {
                (row.ATTEMPTS || []).forEach((attempt) => {
                    rows.push({
                        ...row,
                        _isAttemptRow: true,
                        _rowId: `${row.EVENT_ID}-attempt-${attempt.ATTEMPT}`,
                        ATTEMPT: attempt.ATTEMPT,
                        SENDING_TIME: attempt.SENDING_TIME,
                        STATUS_CODE: attempt.STATUS_CODE,
                        RESPONSE_MESSAGE: attempt.RESPONSE_MESSAGE,
                    });
                });
            }
        });
        return rows;
    }, [deliveriesData?.data, expandedEvents, mode]);

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('webhookRecords.title') }));
    }, [dispatch]);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        loadTableData();
    }, [debouncedTableOptions]);

    const getAllGridParams = () => ({
        filters: [[...(tableOptions.filters || [])]],
        sorts: [tableOptions.sort],
        page: tableOptions.page,
        items: tableOptions.items,
        columns: [
            ...REQUIRED_WEBHOOK_DELIVERIES_GRID_COLUMNS,
            ...tableOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key),
        ].filter(filterUnique),
    });

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(
                getWebhookDeliveriesGridFetch({
                    params: getAllGridParams(),
                    mode,
                })
            );
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const handleChangeMode = (newMode: WebhookDeliveryGridMode) => {
        sessionStorage.setItem(MODE_SESSION_NAME, newMode);
        setMode(newMode);
        setExpandedEvents({});
        setTableOptions((prev) => ({
            ...prev,
            page: 1,
        }));
    };

    const reloadTableData = () => {
        dispatch(getWebhookDeliveriesGridFetch(undefined));
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const toggleExpanded = (eventId: string) => {
        setExpandedEvents((prev) => ({
            ...prev,
            [eventId]: !prev[eventId],
        }));
    };

    const handleRetry = (row: TableRowType) => {
        setConfirmParams({
            title: translate('webhookRecords.action.retry'),
            content: translate('webhookRecords.action.retryConfirm'),
            action: () => dispatch(retryWebhookDeliveryFetch(String(row.EVENT_ID))),
        });
    };

    const customRender: CustomRenderType = (column, row): ReactNode | string => {
        switch (column.key) {
            case 'EVENT_ID':
                return (
                    <TableRouterLink
                        to={`/settings/webhook_records/${row.EVENT_ID}`}
                        data-testid={`webhook-record-event-${row._rowId || row.EVENT_ID}`}
                    >
                        {row.EVENT_ID}
                    </TableRouterLink>
                );
            case 'STATUS_CODE': {
                const deliveryRow = row as WebhookDeliveryRowType;
                return (
                    <span style={{ color: getStatusColor(row.STATUS_CODE as number) }}>
                        {row.STATUS_CODE}
                        {isAutoRetryInProgress(deliveryRow) && deliveryRow.NEXT_RETRY_AT && (
                            <>
                                <br />
                                <span style={{ color: '#666', fontSize: '12px' }}>
                                    {translate('webhookRecords.nextRetryAt', {
                                        time: moment(deliveryRow.NEXT_RETRY_AT).format('DD.MM.YYYY HH:mm'),
                                    })}
                                </span>
                            </>
                        )}
                    </span>
                );
            }
            case 'RETRY_RELATION': {
                if (row._isAttemptRow) {
                    return '';
                }
                const deliveryRow = row as WebhookDeliveryRowType;
                if (deliveryRow.RETRY_OF_EVENT_ID) {
                    return (
                        <span>
                            {translate('webhookRecords.table.retryOf')}{' '}
                            <TableRouterLink to={`/settings/webhook_records/${deliveryRow.RETRY_OF_EVENT_ID}`}>
                                {deliveryRow.RETRY_OF_EVENT_ID}
                            </TableRouterLink>
                        </span>
                    );
                }
                return '';
            }
            case 'RESPONSE_MESSAGE':
                return (
                    <TruncatedCopyCell
                        value={row.RESPONSE_MESSAGE as string}
                        data-testid={`webhook-record-response-${row._rowId || row.EVENT_ID}`}
                    />
                );
            case 'PAYLOAD':
                return (
                    <TruncatedCopyCell
                        value={stringifyPayload(row.PAYLOAD)}
                        data-testid={`webhook-record-payload-${row._rowId || row.EVENT_ID}`}
                    />
                );
            case 'ATTEMPT_COUNT':
                if (mode !== 'grouped' || row._isAttemptRow) {
                    return row.ATTEMPT ?? row.ATTEMPT_COUNT;
                }
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <IconButton
                            size="small"
                            onClick={() => toggleExpanded(String(row.EVENT_ID))}
                            aria-label={translate('webhookRecords.action.toggleAttempts')}
                            data-testid={`webhook-record-expand-${row.EVENT_ID}`}
                        >
                            {expandedEvents[String(row.EVENT_ID)] ? (
                                <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                            )}
                        </IconButton>
                        {row.ATTEMPT_COUNT}
                    </span>
                );
            default:
                return undefined;
        }
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <WebhookEditIcon />,
            label: translate('webhookRecords.action.recordDetail'),
            getLink: (row) => `/settings/webhook_records/${row.EVENT_ID}`,
            hiddenFce: (row) => !permissionDetail || !!row._isAttemptRow,
        },
        {
            icon: <ReloadIcon />,
            label: translate('webhookRecords.action.retry'),
            onClick: handleRetry,
            hiddenFce: (row) =>
                !permissionUpdate || !canRetryDelivery(row as WebhookDeliveryRowType) || !!row._isAttemptRow,
        },
    ];

    return (
        <div data-testid="webhook-records-list">
            <TopBlock reloadTableData={reloadTableData} mode={mode} onChangeMode={handleChangeMode} />
            <div data-testid="webhook-records-table">
            <Table
                allColumns={tableColumns}
                ref={tableRef}
                rowIdKey="_rowId"
                selectRowIdKey="_rowId"
                rows={displayRows.map((row) => ({
                    ...row,
                    _rowId: row._rowId || row.EVENT_ID,
                }))}
                loading={loading && !deliveriesData}
                columsLabelPrefix="webhookRecords.table"
                options={tableOptions}
                totalCount={deliveriesData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={tableRowActions}
                selectOptions={{
                    webhookTypeCodes,
                    webhookMethodCodes,
                }}
                userSettingsName={
                    mode === 'grouped'
                        ? 'showWebhookRecordsGroupedTableColumns'
                        : 'showWebhookRecordsAttemptsTableColumns'
                }
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                optionsSessionName={SESSION_NAME}
                heightVariant="top-tab-2"
                fullHeight
                customRender={customRender}
                rowSxFce={(row) => (row._isAttemptRow ? { backgroundColor: '#fafafa' } : {})}
            />
            </div>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </div>
    );
};

export default WebhookRecordsListContainer;
